import type { JSX } from 'react';

import Linkify from 'linkify-react';

interface LinkHighlighterProps {
  text?: string;
  placeholder?: string;
  className?: string;
}

// Common file extensions that should not be treated as links
const FILE_EXTENSIONS = new Set([
  // Documents
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv', 'odp', 'odt', 'ods', 'txt', 'rtf',
  // Archives
  'zip', 'rar', '7z', 'tar', 'gz', 'bz2',
  // Media
  'mp3', 'mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'wav', 'ogg',
  'jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'ico', 'tiff', 'heic',
  // Executables
  'exe', 'msi', 'dmg', 'pkg', 'deb', 'rpm', 'apk',
]);

/**
 * Checks if a string looks like a file name (ends with a known file extension).
 */
const isFileName = (value: string): boolean => {
  const match = value.match(/\.([a-z0-9]+)$/i);
  const extension = match?.[1];
  return extension ? FILE_EXTENSIONS.has(extension.toLowerCase()) : false;
};

export const LinkHighlighter = ({ text, placeholder, className }: LinkHighlighterProps) => {
   
  if (!text) return <p className={className}>{placeholder}</p>;

  const lines = text.split(/\n/g);

  // Function to render a word or a URL
  const renderWord = (word: string, key: string) => {
    // If the whole word looks like a file name, skip Linkify entirely
    // This prevents linkify from detecting URLs within file names (e.g., shopee.loga.vn in file.vn.xlsx)
    if (isFileName(word)) {
      return <span key={key}>{word}</span>;
    }

    return (
      <Linkify
        options={{
          target: '_blank',
          rel: 'noopener noreferrer',
          className: 'text-link',
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
        }}
        key={key}
      >
        {word}
      </Linkify>
    );
  };

  const renderLine = (line: string, lineIndex: number) => {
    const elements: JSX.Element[] = [];
    let word = '';

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === ' ') {
        // If a space is encountered, process the word and add a space
        if (word) {
          elements.push(renderWord(word, `${lineIndex}-${i}`));
          word = ''; // Reset word after rendering
        }
        elements.push(<span key={`${lineIndex}-${i}-space`}>&nbsp;</span>); // Add space
      } else {
        // Build up the word
        word += char;
      }
    }

    // Handle the last word in the line
    if (word) {
      elements.push(renderWord(word, `${lineIndex}-last`));
    }

    return <div key={lineIndex}>{elements}</div>;
  };

  return lines.map((line, index) => {
    const lineElements = renderLine(line, index);
    const isEmptyLine = line.trim() === '';

    return (
      <p key={index} className={className}>
        {lineElements}
        {isEmptyLine && <br />} {/* Add <br /> for empty lines */}
      </p>
    );
  });
};
